<template>
  <div class="invoice-add-container">
    <b-row>
      <b-col :lg="12">
        <div class="drip-form-container">
          <div class="drip-form">
            <b-row>
              <b-col>
                <h3 class="drip-h3 mb-1">
                  {{ $t('invoices.add.titles.uploadDocs') }}
                </h3>
                <div class="drip-body mb-3 font-weight-400">
                  Files should be in PDF format and should not exceed 50 MB in
                  size.
                </div>
              </b-col>
            </b-row>
            <b-row v-if="filesKey && filesKey.length">
              <b-col
                v-for="(item, i) in filesKey"
                :key="i"
                :lg="12"
                :style="item && item.name_key ? 'margin-bottom: 12px;' : ''"
              >
                <uploads
                  v-if="item && item.name_key"
                  :index="0"
                  :title="item.title"
                  :subtext="
                    multiple.includes(item.name_key)
                      ? `Note: If you're submitting multiple files, please select them all at once.`
                      : ''
                  "
                  :itemKey="item.key"
                  :docTag="item.name_key"
                  :uploadsSaved="uploadsSaved"
                  :isOptional="false"
                  :documentId="item.id"
                  :uploadBorder="true"
                  acceptedFiles="application/pdf"
                  :multiple="multiple"
                  :apiUrl="apiUrl"
                  :callback="loadInvoice"
                >
                  <template v-slot:body>
                    <div v-if="item.name_key === 'commercial_invoice'">
                      <div
                        class="d-flex flex-grow-1 drip-label font-weight-700 color-noble-blue-500 w-100 my-1"
                      >
                        {{ $t('invoices.upload.commercial_invoice_title') }}
                      </div>
                      <div class="d-flex no-wrap">
                        <div
                          class="d-flex flex-grow-1 drip-body-small font-weight-400 color-black"
                        >
                          {{
                            $t('invoices.upload.commercial_invoice_sub_text')
                          }}
                        </div>
                        <div
                          class="d-flex align-items-center color-wealthy-green-500 drip-body-small font-weight-700"
                        >
                          <copy-clip-button
                            :copy-text="
                              $t('invoices.upload.commercial_invoice_sub_text')
                            "
                            class="ml-2"
                          />
                        </div>
                      </div>
                    </div>
                  </template>
                </uploads>
              </b-col>
            </b-row>
            <b-row v-else>
              <b-col>
                <loader :height="500" />
              </b-col>
            </b-row>
          </div>
          <div class="drip-form-actions">
            <b-row>
              <b-col>
                <b-link
                  class="mr-3 btn btn-light-purple"
                  :loading="false"
                  @click="navigateBack"
                >
                  <span class="d-md-inline-block d-none">{{
                    $t('invoices.add.actions.back')
                  }}</span>
                  <b-icon-chevron-left class="d-md-none" font-scale="0.9" />
                </b-link>
              </b-col>
            </b-row>
            <br />
            <b-row>
              <b-col>
                <form-button
                  :loading="false"
                  variant="secondary"
                  @click="nextClick"
                >
                  <span class="d-md-inline-block d-none">{{
                    $t('invoices.add.actions.next')
                  }}</span>
                  <b-icon-chevron-right class="d-md-none" font-scale="0.9" />
                </form-button>
              </b-col>
            </b-row>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-modal ref="confirmModal" :hide-footer="true" class="confirm-modal">
      <p class="confirm-modal-text lead">
        {{ $t('invoices.upload.proceed') }}
      </p>
      <b-button
        variant="primary mr-2"
        @click="$router.push({ name: 'dashboard' })"
        v-track-click
        id="invoices-upload-later"
      >
        {{ $t('invoices.upload.toDashBoard') }}
      </b-button>
      <form-button
        variant="secondary"
        @click="$router.push({ name: 'invoicesAdd' })"
        v-track-click
        class="d-none d-md-inline-block"
        id="invoices-upload-save-new"
      >
        {{ $t('invoices.upload.addMoreInvoices') }}
      </form-button>
    </b-modal>
  </div>
</template>
<script>
import Loader from '@/components/common/Loader'
import FormButton from '@/components/common/FormButton'
import invoiceService from '@/services/invoice'
import { mapActions } from 'vuex'
import Uploads from '../../../common/Uploads'
import CopyClipButton from '@/components/common/CopyClipButton'

export default {
  props: ['form', 'uploadsSaved'],
  data() {
    return {
      loading: false,
      invoice: {},
      prevStep: 'funding-details',
      nextStep: 'review',
      uploadInProgress: 0,
      // todo: don't use state
      newInvoiceRequested: false,
      filesKey: [],
      uploadsDocs: null,
      apiUrl: `${process.env.VUE_APP_DRIP_API_HOST}/v2/business/invoices/${this.$route.params.id}/documents`,
      multiple: [
        'shipping_bill_s_india',
        'purchase_order_s',
        'bill_s_of_lading',
        'packing_list',
        'pedimento'
      ] // name_key
    }
  },
  mounted() {
    this.$mpTrack(`Add Invoice - Upload Docs Step Loaded`)
    this.loadInvoice()
  },
  computed: {
    isDone() {
      const uploads = this.uploadsDocs
      if (uploads) {
        return this.invoice['basic_documents_uploaded?']
      }
      return false
    }
  },
  methods: {
    copyToClip(e) {
      e.stopPropagation()
      const val = this.$t('invoices.upload.commercial_invoice_sub_text')
      if (navigator.clipboard) {
        navigator.clipboard
          .writeText(val)
          .then(() => {
            // Successful copy
            this.$notify({
              title: 'Copied to clipboard!',
              type: 'success'
            })
          })
          .catch((err) => {
            // Clipboard write failed
            console.error('Failed to copy text:', err)
            this.$notify({
              title: 'Failed to copy to clipboard!',
              type: 'error'
            })
          })
      } else {
        const textArea = document.createElement('textarea')
        textArea.value = val

        // Move textarea out of the viewport so it's not visible
        textArea.style.position = 'absolute'
        textArea.style.left = '-999999px'

        document.body.prepend(textArea)
        textArea.select()

        try {
          document.execCommand('copy')
          this.$notify({
            title: 'Copied to clipboard!',
            type: 'success'
          })
        } catch (error) {
          console.error(error)
          this.$notify({
            title: 'Failed to copy to clipboard!',
            type: 'error'
          })
        } finally {
          textArea.remove()
        }
      }
    },
    uploadTasks(invoice) {
      if (!invoice.task_flows) {
        return []
      }
      // fetch task flows starting with 'upload_'
      const upload_ = invoice.task_flows.filter((task) => {
        const regex = new RegExp(invoice.doc_set_2.join('|'), 'gi')
        const except = invoice.doc_set_2.length > 0 && task.uid.match(regex)
        return task.uid && task.uid.includes('upload_') && !except
      })
      this.setUploads = upload_

      const documentsMap = upload_.map((x) => x.upload_docs[0].name_key)

      upload_.forEach((x) => {
        const doc = x.upload_docs[0]
        const newDoc = {
          docId: x.documents[0] && x.documents[0].id, // document id
          id: x.id, // task id
          title: doc.name,
          name_key: doc.name_key,
          key: doc.key
        }
        this.filesKey[documentsMap.findIndex((item) => item === doc.name_key)] =
          newDoc
      })

      // To keep the order fixed after uploading
      this.filesKey = this.filesKey.sort((a, b) => a.id - b.id)

      const newInvoiceSaved = [...this.uploadsSaved]
      // List of documents from task_flows
      newInvoiceSaved[0].task_flows = upload_

      upload_.forEach((task) => {
        let nameKey =
          task && task.upload_docs[0] && task.upload_docs[0].name_key

        newInvoiceSaved[0][nameKey] =
          invoice.documents_public &&
          invoice.documents_public
            .filter((file) => {
              const key = ['shipping_bill_s_india', 'pedimento'].includes(
                nameKey
              )
                ? 'shipping_bill_s'
                : nameKey
              return file.tags_value_key === key
            })
            .map((x) => {
              return {
                docId: x.id, // document id
                url: x.file_url,
                name: x.file_identifier,
                type: x.tags_value,
                key: x.tags
              }
            })
      })

      // emit update event
      this.$emit('updateInvoiceSaved', newInvoiceSaved)

      return upload_
    },
    ...mapActions(['setStep', 'setFormData']),
    navigateBack() {
      this.$mpTrack(`Add Invoice - navigate back to ${this.prevStep}`)
      this.navigate('invoicesAdd', this.$route.params.id, this.prevStep)
    },
    success(task) {
      task.status = 'done'
    },
    loadInvoice() {
      return invoiceService
        .fetchInvoicesById(this.$route.params.id, {
          include: ['docs_tagged', 'task_flows']
        })
        .then((res) => {
          this.invoice = res.data
          this.uploadsDocs = this.uploadTasks(res.data)
          if (res.data['pdc_applicable?']) {
            this.$emit('get-pdc-data')
          }
        })
        .catch((e) => {
          console.log(e)
        })
    },
    navigate(component, id, value) {
      this.$router.push({
        name: component,
        params: {
          id: id || '',
          value: value || ''
        }
      })
    },
    nextClick() {
      if (!this.isDone) {
        this.$mpTrack(
          'Add Invoice - Documents validation error - files not uploaded'
        )
        return this.$refs.confirmModal.show()
      }
      this.showNextStep()
    },
    showNextStep() {
      this.setStep(this.nextStep)
      this.$mpTrack('Add Invoice - Required Documents Uploaded')
      this.navigate('invoicesAdd', this.$route.params.id, this.nextStep)
    },
    getUploadedFile(key) {
      return (
        this.invoice &&
        this.invoice.documents_public &&
        (this.invoice.documents_public.find((d) =>
          key.startsWith(d.tags_normal)
        ) ||
          {})
      )
    }
  },
  components: {
    Loader,
    FormButton,
    Uploads,
    CopyClipButton
  }
}
</script>
<style lang="scss">
.confirm-modal {
  .modal-dialog {
    width: 720px;
    max-width: 100%;
    .modal-body {
      padding: 1.67rem;
    }
  }
}
.pointerEventAuto {
  pointer-events: auto;
}
.h-36 {
  height: 36px !important;
}
</style>
